<template>
    <FormSection
        :full-height="true"
        title="Reassign Task"
        :application-id="visitDetails.Application"
        :show-product-pill="true"
        :show-background="true"
        :use-gray-theme="true"
    >
        <template #content>
            <div id="hc-reassign">
                <section
                    class="dialog container p-5 d-flex flex-column text-center text-primary loader-section"
                    v-if="isLoading"
                >
                    <i class="fa fa-spinner fa-spin font-size-5xl me-2 animated slow flash infinite"></i>
                    <span class="mt-1 big-text">
                        <b>Loading...</b>
                    </span>
                </section>
                <section v-else>
                    <div class="dialog container d-flex flex-column m-0 px-4 py-2">
                        <div class="d-flex align-items-center justify-content-between">
                            <div class="col-6 pb-1 mb-2 mt-2" for="user-task"><strong>Task</strong></div>
                            <div class="col-6 pb-1 mb-2 mt-2">{{ visitDetails.TaskName }}</div>
                        </div>
                        <div class="d-flex align-items-center justify-content-between">
                            <div class="col-6 pb-1 mb-2" for="user-task"><strong>Scheduled Date/Time</strong></div>
                            <div class="col-6 pb-1 mb-2" v-if="visitDetails.StartDateTime">
                                {{ formatDateFromDate(visitDetails.StartDateTime) }}
                                {{
                                    formatTime(visitDetails.StartDateTime) +
                                    " - " +
                                    formatTime(visitDetails.EndDateTime)
                                }}
                            </div>
                            <div class="col-6 pb-1 mb-2" v-else>
                                {{ formatDateFromDate(visitDetails.StartDate) }}
                                {{ formatTime(visitDetails.StartTime) }}
                                {{ visitDetails.EndTime ? " - " + formatTime(visitDetails.EndTime) : "" }}
                            </div>
                        </div>
                        <div class="d-flex align-items-center justify-content-between">
                            <div class="col-6 pb-1 mb-2" for="user-name"><strong>Client</strong></div>
                            <div class="col-6 pb-1 mb-2">{{ visitDetails.FirstName }} {{ visitDetails.LastName }}</div>
                        </div>
                        <div class="d-flex align-items-center justify-content-between">
                            <div class="col-6 pb-1 mb-2" for="user-name"><strong>Scheduled User/Employee</strong></div>
                            <div
                                class="col-6 pb-1 mb-2"
                                v-if="!visitDetails.UserFirstName && !visitDetails.UserLastName"
                            >
                                Unassigned
                            </div>
                            <div v-else class="col-6 pb-1 mb-2">
                                {{ visitDetails.UserFirstName }} {{ visitDetails.UserLastName }}
                            </div>
                        </div>
                        <div class="d-flex align-items-center justify-content-between">
                            <div :class="`${preSelectedEmpId ? 'col-6 pb-1 mb-2' : 'col-6 mb-4'}`" for="user-new">
                                <strong>New User/Employee</strong><span class="text-danger">*</span>
                            </div>
                            <div class="col-6 pb-1 mb-2">
                                <div v-if="preSelectedEmpId">
                                    {{ employeeName.DisplayName }}
                                </div>
                                <template v-else>
                                    <MultiSelectDropdown
                                        class="text-center mb-2"
                                        :selected-items="selectedConflictTypes"
                                        :options="conflictTypes"
                                        :show-icons="true"
                                        :disabled="!canShowPremium"
                                        v-on:selectedItemsUpdated="selectedItemsUpdated"
                                        :find-a-match="true"
                                    />
                                    <TypeaheadInput
                                        v-if="!isEmployeeListLoading"
                                        :getData="employeeListFilter"
                                        :value="scheduleEmployee"
                                        v-on:valueChanged="changescheduleEmployee"
                                        placeholder="Type to Select User"
                                        :show-all-by-default="true"
                                        :open-on-focus="true"
                                        dropdown-menu-class="custom-menu"
                                        :class="`${!v$.visitDetails.EmpId.$error ? '' : 'field-invalid'}`"
                                        @blur="v$.visitDetails.EmpId.$touch"
                                    />
                                    <span v-if="v$.visitDetails.EmpId.$error" class="field-invalid"
                                        >New User/Employee is Required.</span
                                    >
                                    <div class="form-group mt-2" v-if="canShowPremium">
                                        <div class="find-ai-button-section" @click="openFindAMatch()">
                                            <button class="button find-ai-button">Find a Match</button>
                                        </div>
                                    </div>
                                    <div class="form-group mt-2" v-else>
                                        <button
                                            class="find-ai-disabled-btn ai-disabled"
                                            v-tooltip="{
                                                title: `Contact Axxess to unlock this <br /> premium Axxess intelligence feature.`,
                                                placement: 'top',
                                                className: 'lock-premium'
                                            }"
                                        >
                                            Find a Match
                                            <img src="../../../../assets/images/lock.svg" />
                                        </button>
                                    </div>
                                </template>
                                <div class="col-6 pb-1 mb-2" v-if="isEmployeeListLoading">
                                    <span class="shimmer-loading w-100"></span>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex align-items-center justify-content-between">
                            <div class="col-6 pb-1 mb-2" for="user-new">
                                <strong>Reason</strong><span class="text-danger">*</span>
                            </div>
                            <div class="col-6 pb-1 mb-2">
                                <select
                                    @change="selectedType()"
                                    v-model="visitDetails.TypeId"
                                    :class="`form-select ${!v$.visitDetails.TypeId.$error ? '' : 'field-invalid'}`"
                                    @blur="v$.visitDetails.TypeId.$touch"
                                >
                                    <option :value="null">Select Reason</option>
                                    <option
                                        v-for="trackType in trackingTypes"
                                        :value="trackType.Id"
                                        :key="trackType.Id"
                                        class="text-capitalize"
                                    >
                                        {{ trackType.Name }}
                                    </option>
                                </select>
                                <span v-if="v$.visitDetails.TypeId.$error" class="field-invalid"
                                    >Reason Type is required.</span
                                >
                            </div>
                        </div>
                        <div class="d-flex align-items-center justify-content-end">
                            <div class="col-6 pb-1 mb-2" v-if="cpyTrackingReason.length > 0">
                                <select
                                    :class="`form-select ${!v$.visitDetails.ReasonId.$error ? '' : 'field-invalid'}`"
                                    v-model="visitDetails.ReasonId"
                                    @blur="v$.visitDetails.ReasonId.$touch"
                                >
                                    <option :value="null">Select Tracking Reason</option>
                                    <option v-for="reason of cpyTrackingReason" :value="reason.Id" :key="reason.Id">
                                        {{ reason.Reason }}
                                    </option>
                                </select>
                                <span v-if="v$.visitDetails.ReasonId.$error" class="field-invalid"
                                    >Tracking Reason is Required.</span
                                >
                            </div>
                        </div>
                        <div class="d-flex justify-content-center mb-3" v-if="!isLoading">
                            <span v-if="isSaving">
                                <button class="btn btn-primary">
                                    <i class="fa fa-spinner fa-spin animated slow flash infinite"></i>
                                </button>
                            </span>
                            <span v-else>
                                <button type="button" class="btn btn-outline-secondary" @click="cancel()">
                                    Cancel
                                </button>
                                <button class="btn btn-primary ms-2" @click="reassignVisit()">Reassign</button>
                                <SaveNotifyUser
                                    :agency-id="visitDetails?.AgencyId"
                                    :button-name="'Reassign and Notify Employee'"
                                    v-on:handleSaveVisit="reassignVisit(true)"
                                />
                            </span>
                        </div>
                    </div>
                </section>
            </div>
        </template>
    </FormSection>
</template>

<style src="./HomeCareReassign.scss"></style>
<script lang="ts" src="./HomeCareReassign.ts"></script>
